import { apiClient, apiClientPaginated } from '@/lib/api-client';

/**
 * Ops simulation T12 — registers (tills) per location and the register
 * session report. Money is in minor units.
 */

export interface Register {
  id: string;
  location_id: string;
  location_name?: string;
  name: string;
  is_active: boolean;
  open_session_id: string | null;
  open_session_number: string | null;
  current_cashier_name: string | null;
}

export interface SessionTotals {
  sales_count: number;
  sales_total: number;
  discount_total: number;
  tax_total: number;
  customers_count: number;
  returns_count: number;
  returns_total: number;
  cancelled_count: number;
  cancelled_total: number;
  net_total: number;
}

export interface SessionPaymentMode {
  payment_method: string;
  collected: number;
  refunded: number;
}

export interface SessionCashierRow extends SessionTotals {
  cashier_id: string | null;
  cashier_name: string | null;
  payment_modes: SessionPaymentMode[];
}

export interface RegisterSessionReportRow {
  id: string;
  location_id: string;
  location_name: string;
  register_id: string;
  register_name: string;
  session_number: string;
  session_date: string;
  status: 'OPEN' | 'CLOSED';
  opened_by_name: string;
  opened_at: string;
  closed_by_name: string | null;
  closed_at: string | null;
  totals: SessionTotals;
  payment_modes: SessionPaymentMode[];
  cashiers: SessionCashierRow[];
  drawer: {
    opening_cash: number;
    expected_cash: number | null;
    counted_cash: number | null;
    difference: number | null;
    difference_reason: string | null;
    approved_by_name: string | null;
  };
}

export interface RegisterSessionReportFilters {
  location_id?: string;
  register_id?: string;
  status?: 'OPEN' | 'CLOSED';
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const registerService = {
  list(locationId: string) {
    return apiClient<Register[]>(`/registers${qs({ location_id: locationId, include_inactive: true })}`);
  },

  create(locationId: string, name: string) {
    return apiClient<Register>('/registers', { method: 'POST', body: { location_id: locationId, name } });
  },

  update(id: string, body: { name?: string; is_active?: boolean }) {
    return apiClient<Register>(`/registers/${id}`, { method: 'PATCH', body });
  },

  report(filters: RegisterSessionReportFilters) {
    return apiClientPaginated<RegisterSessionReportRow[]>(`/register-sessions/report${qs({ ...filters })}`);
  },
};
